import { useEffect, useMemo, useRef, useState } from 'react';

import { Download, Eye, Trash, X } from 'phosphor-react';

import { PlusCircle } from 'react-feather';

import { AppButton } from '../AppButton/AppButton';

import { COLORS } from '../../../utils/colors';

import './AppNewSupplierJobModal.scss';
import {
  getAllStates,
  getStateAbbreviation,
  SupplierJobUpdate,
  Buyer,
  SupplierJobApiResponse,
  SupplierJobAddressDetails,
} from '@dill/dill-shared';
import { PartialSupplierOwnerVerify, SupplierVerifiedJobFormData } from './constants';
import VerifySupplierJob from './VerifyJob';
import { AppInputField } from '../AppInputField/AppInputField';
import { on } from 'events';
import { VerifyJobRequestData } from '../../../redux/services/verifyAddressService';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { AppCheckBox } from '../AppCheckBox/AppCheckBox';
import EditBuyerModal from '../../modules/suppliers/EditBuyerModal/EditBuyerModal';
import { AppDropDown2 } from '../AppDropDown2/AppDropDown2';
import { getSupplierBuyers } from '../../../redux/services/supplierBuyersService';
import { twMerge } from 'tailwind-merge';
import AppFilePicker from '../AppFilePicker/AppFilePicker';
import { AppImagePreviewModal } from '../AppImagePreviewModal/AppImagePreviewModal';
import { AppPdfPreviewModal } from '../AppPdfPreviewModal/AppPdfPreviewModal';
import { downloadFile } from '../../../utils/helpers';

export type VerifiedJobDataPick = Pick<
  SupplierJobUpdate,
  | 'name'
  | 'jobNumber'
  | 'customerJobNumber'
  | 'jobAddress'
  | 'jobCity'
  | 'jobState'
  | 'jobZipCode'
  | 'buyerId'
  | 'owners'
  | 'GCs'
  | 'lenders'
  | 'apn'
  | 'constructionMonitorPermitId'
  | 'estimatedMaterialAmount'
  | 'jobType'
  | 'jobSector'
  | 'customerType'
  | 'jobFurnishings'
  | 'hasNoLender'
  | 'jobCounty'
  | 'jobLegalDescription'
  | 'isJobBonded'
  | 'sureties'
  | 'isGCUnknown'
  | 'isOwnerUnknown'
  | 'isLenderUnknown'
  | 'isSurityUnknown'
>;

interface SupplierVerifyJobFormFunctions {
  supplierJobData: PartialSupplierOwnerVerify;
  onClose: () => void;
  onBack: () => void;
  onUseVerifiedData: (data: {
    formData: Record<string, any>;
    attachments?: { attachmentFiles: File[]; attachmentUrls: { name: string; url: string }[] };
  }) => void;
  onManualDataUpdate: (data: {
    formData: Record<string, any>;
    attachments?: { attachmentFiles: File[]; attachmentUrls: { name: string; url: string }[] };
  }) => void;
  editMode: 'ADD' | 'EDIT';
  onBuyerEdit?: () => void;
  onVerify: (data: Record<string, any>, dataOriginal: Record<string, any>) => void;
  verifying: boolean;
  onRequestInfoSheet: (data: any) => void;
  verifyJobCheckBox: boolean;
}

enum ModalState {
  ADD_JOB,
  VERIFY_JOB,
  VERIFY_OWNER,
  VERIFY_OWNER_MANUAL,
}

type SupplierVerifyJobFormProps = SupplierVerifyJobFormFunctions & SupplierVerifiedJobFormData;

const SupplierVerifyJobForm = (props: SupplierVerifyJobFormProps) => {
  const {
    onClose,
    onBack,
    onUseVerifiedData,
    onManualDataUpdate,
    onBuyerEdit,
    onVerify,
    onRequestInfoSheet,
    jobAddress,
    jobCity,
    jobState,
    jobZipCode,
    owners,
    GCs,
    verifiedProperty,
    jobNumber,
    name,
    buyerId,
    supplierJobData,
    constructionMonitorPermitId,
    estimatedMaterialAmount,
    jobType,
    jobSector,
    customerType,
    jobFurnishings,
    hasNoLender,
    editMode,
    verifying,
    verifyJobCheckBox,
    customerJobNumber,
  } = props;

  const dispatch = useAppDispatch();
  const [state, setState] = useState<ModalState>(ModalState.VERIFY_JOB);
  const [formState, setFormState] = useState<VerifiedJobDataPick>({});
  const [formAddressState, setAddressFormState] = useState<{
    GCs: SupplierJobAddressDetails[];
    owners: SupplierJobAddressDetails[];
    lenders?: SupplierJobAddressDetails[];
    sureties?: SupplierJobAddressDetails[];
  }>({
    GCs: [],
    owners: [],
  });
  const [showErrorAbove, setShowErrorAbove] = useState(false);
  const [isEditBuyerModalOpen, setIsEditBuyerModalOpen] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState<Buyer | null>(null);
  const [ownerAndGCSame, setOwnerAndGCSame] = useState(false);
  const [formAddressError, setAddressError] = useState<{
    GCs: string;
    owners: string;
    lenders: string;
    sureties: string;
  }>({
    GCs: '',
    owners: '',
    lenders: '',
    sureties: '',
  });
  const [errors, setErrors] = useState<VerifiedJobDataPick>({});

  const { user } = useAppSelector((globalState) => globalState.auth);
  const currentSupplier = useAppSelector((globalState) => globalState.auth.currentSupplier);
  const { supplierBuyers } = useAppSelector((globalState) => globalState.supplierBuyers);
  const { supplierJobsMap } = useAppSelector((globalState) => globalState.supplierJobs);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [attachmentUrls, setAttachmentUrls] = useState<
    { name?: string; url?: string; fileType?: string }[]
  >([]);

  const [showPreviewImage, setShowPreviewImage] = useState(false);
  const [previewattachmentUrl, setPreviewAttachmentUrl] = useState('');
  const [isAppPdfPreviewModalOpen, setIsAppPdfPreviewModalOpen] = useState(false);
  const [isGCAutoCompleteOpen, setIsGCAutoCompleteOpen] = useState('');
  const [isOwnerAutoCompleteOpen, setIsOwnerAutoCompleteOpen] = useState('');

  const gcRef = useRef<HTMLDivElement | null>(null);
  const ownerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (gcRef.current && !gcRef.current.contains(event.target as Node)) {
        setIsGCAutoCompleteOpen('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ownerRef.current && !ownerRef.current.contains(event.target as Node)) {
        setIsOwnerAutoCompleteOpen('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const changeGCData = (changeName: string, value: string | number | boolean) => {
    if (
      (changeName === 'buyerId' || changeName === 'customerType') &&
      formAddressState?.GCs?.[0]?.name?.trim() === ''
    ) {
      if (formState.customerType === 'GC' || value === 'GC') {
        let changeBuyerId = formState.buyerId;
        if (changeName === 'buyerId') {
          changeBuyerId = value as string;
        }
        const foundSupplierBuyer = supplierBuyers.find((supBuy) => supBuy.id === changeBuyerId);
        if (foundSupplierBuyer) {
          const data = [
            {
              name: foundSupplierBuyer?.name || '',
              address: foundSupplierBuyer?.address || '',
              city: foundSupplierBuyer?.city || '',
              state: foundSupplierBuyer?.state || '',
              zipCode: foundSupplierBuyer?.zipCode || '',
            },
          ];
          setAddressFormState((prevState) => ({
            ...prevState,
            GCs: data,
          }));
        }
      }
    }
  };

  const handleUpdateSupplierJobAddress = ({
    field,
    value,
    index,
    key,
  }: {
    field: keyof SupplierJobAddressDetails;
    value: string;
    index: number;
    key: 'GCs' | 'owners' | 'lenders' | 'sureties';
  }) => {
    setAddressFormState((prevState) => {
      const updated = [...(prevState[key] ?? [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prevState, [key]: updated };
    });
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const handleAddAddress = (key: 'GCs' | 'owners' | 'lenders' | 'sureties') => {
    let moreFields = {};
    if (key === 'sureties') {
      moreFields = {
        bondNumber: '',
      };
    }
    setAddressFormState((prev) => ({
      ...prev,
      [key]: [
        ...(prev[key] ?? []),
        {
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          ...moreFields,
        },
      ],
    }));
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const handleRemoveAddress = (index: number, key: 'GCs' | 'owners' | 'lenders' | 'sureties') => {
    setAddressFormState((prev) => {
      const previousState = [...(prev[key] ?? [])];
      const updated = previousState.filter(
        (_: SupplierJobAddressDetails, i: number) => i !== index
      );
      return { ...prev, [key]: updated };
    });
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const onChange2 = (data: { name: string; value: string | number | boolean }) => {
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
    setFormState((prevState) => ({
      ...prevState,
      [data.name]: data.value,
    }));
    changeGCData(data.name, data.value);
  };

  const allStates = useMemo(() => {
    return getAllStates();
  }, []);

  const changeAutoState = () => {
    setState(ModalState.VERIFY_OWNER);
  };

  const changeManualState = () => {
    setState(ModalState.VERIFY_OWNER_MANUAL);
  };

  useEffect(() => {
    if (!verifyJobCheckBox) {
      setState(ModalState.VERIFY_OWNER_MANUAL);
    }
  }, []);

  useEffect(() => {
    if (editMode === 'EDIT') {
      setState(ModalState.VERIFY_OWNER_MANUAL);
    }
  }, []);

  const closeModal = () => {
    setFormState({});
    setState(ModalState.VERIFY_JOB);
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
    onClose();
  };

  const handleBack = () => {
    if (
      (state === ModalState.VERIFY_OWNER || state === ModalState.VERIFY_OWNER_MANUAL) &&
      currentSupplier?.ownerVerificationEnabled &&
      verifyJobCheckBox
    ) {
      setState(ModalState.VERIFY_JOB);
    } else {
      onBack();
    }
  };

  const validateInputs = () => {
    const newErrors = {} as VerifiedJobDataPick;

    let isValid = true;

    if (
      formState.estimatedMaterialAmount === '' ||
      Number(formState.estimatedMaterialAmount) <= 0
    ) {
      newErrors.estimatedMaterialAmount =
        formState.estimatedMaterialAmount === ''
          ? 'Estimated material amount is required.'
          : 'Estimated material amount should be more than 0.';
      isValid = false;
    }
    if (formState.jobFurnishings?.trim() === '') {
      newErrors.jobFurnishings = 'Job Furnishings is required.';
      isValid = false;
    }
    if (formState.name?.trim() === '' && editMode === 'EDIT') {
      newErrors.name = 'Job name is required.';
      isValid = false;
    }
    if (formState.jobAddress?.trim() === '' && editMode === 'EDIT') {
      newErrors.jobAddress = 'Job address is required.';
      isValid = false;
    }
    if (formState.jobCity?.trim() === '' && editMode === 'EDIT') {
      newErrors.jobCity = 'Job city is required.';
      isValid = false;
    }
    if (formState.jobState?.trim() === '' && editMode === 'EDIT') {
      newErrors.jobState = 'Job state is required.';
      isValid = false;
    }
    if (formState.jobZipCode?.trim() === '' && editMode === 'EDIT') {
      newErrors.jobZipCode = 'Job zip code is required.';
      isValid = false;
    }
    if (formState.buyerId?.trim() === '' && editMode === 'EDIT') {
      newErrors.buyerId = 'Select a buyer.';
      isValid = false;
    }
    if (
      formState.jobState?.trim() === 'AZ' &&
      (formState.jobCounty?.trim() === '' || !formState.jobCounty)
    ) {
      newErrors.jobCounty = 'Job county is required.';
      isValid = false;
    }
    if (
      formState.jobState?.trim() === 'AZ' &&
      (formState.jobLegalDescription?.trim() === '' || !formState.jobLegalDescription)
    ) {
      newErrors.jobLegalDescription = 'Job legal description is required.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateInputsVerify = () => {
    const newErrors = {} as VerifiedJobDataPick;

    let isValid = true;
    if (formState.jobAddress?.trim() === '' && editMode === 'EDIT') {
      newErrors.jobAddress = 'Job address is required.';
      isValid = false;
    }
    if (formState.jobCity?.trim() === '' && editMode === 'EDIT') {
      newErrors.jobCity = 'Job city is required.';
      isValid = false;
    }
    if (formState.jobState?.trim() === '' && editMode === 'EDIT') {
      newErrors.jobState = 'Job state is required.';
      isValid = false;
    }
    if (formState.jobZipCode?.trim() === '' && editMode === 'EDIT') {
      newErrors.jobZipCode = 'Job zip code is required.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAddressInputs = () => {
    const newErrors = {} as {
      GCs: string;
      owners: string;
      lenders: string;
      sureties: string;
    };

    let isValid = true;

    if (formAddressState.GCs.length > 0 && !formState.isGCUnknown) {
      formAddressState.GCs.some((gC) => {
        if (gC.name?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.address?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.city?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.state?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.zipCode?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
      });
    }

    if (formAddressState.owners.length > 0 && !formState.isOwnerUnknown) {
      formAddressState.owners.some((owner) => {
        if (owner.name?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.address?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.city?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.state?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.zipCode?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
      });
    }
    if (
      formAddressState.lenders &&
      formAddressState.lenders.length > 0 &&
      !formState.hasNoLender &&
      !formState.isLenderUnknown
    ) {
      formAddressState.lenders.some((lender) => {
        if (lender.name?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.address?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.city?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.state?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.zipCode?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
      });
    }

    if (
      formAddressState.sureties &&
      formAddressState.sureties.length > 0 &&
      !formState.isSurityUnknown &&
      formState.isJobBonded
    ) {
      formAddressState.sureties.some((surety) => {
        if (surety.name?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.address?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.city?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.state?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.zipCode?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.bondNumber?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
      });
    }

    setAddressError(newErrors);
    return isValid;
  };

  const onSubmitData = () => {
    validateInputs();
    validateAddressInputs();
    if (!validateInputs()) {
      setShowErrorAbove(true);
      return;
    }
    if (!validateAddressInputs()) {
      setShowErrorAbove(true);
      return;
    }
    setShowErrorAbove(false);
    const additionalData = {
      GCs: !formState.isGCUnknown
        ? formAddressState.GCs
        : [
            {
              name: '',
              address: '',
              city: '',
              state: '',
              zipCode: '',
            },
          ],
      owners: !formState.isOwnerUnknown
        ? formAddressState.owners
        : [
            {
              name: '',
              address: '',
              city: '',
              state: '',
              zipCode: '',
            },
          ],
      lenders: !formState.isLenderUnknown
        ? formAddressState.lenders
        : [
            {
              name: '',
              address: '',
              city: '',
              state: '',
              zipCode: '',
            },
          ],
      sureties: !formState.isSurityUnknown
        ? formAddressState.sureties
        : [
            {
              name: '',
              address: '',
              city: '',
              state: '',
              zipCode: '',
              bondNumber: '',
            },
          ],
    };

    const newFormStateData = {
      ...formState,
      ...additionalData,
      lenders: formState.hasNoLender ? [] : additionalData.lenders,
      sureties: formState.isJobBonded ? additionalData.sureties : [],
    };
    if (state === ModalState.VERIFY_OWNER) {
      const updatdData = {
        ...newFormStateData,
        jobAddress: verifiedProperty?.address ? verifiedProperty.address : jobAddress,
        jobCity: verifiedProperty?.city ? verifiedProperty.city : jobCity,
        jobState: verifiedProperty?.state
          ? getStateAbbreviation(verifiedProperty.state || '')
          : getStateAbbreviation(jobState || ''),
        jobZipCode: verifiedProperty?.zipCode ? verifiedProperty.zipCode : jobZipCode,
        apn: verifiedProperty?.apn ? verifiedProperty.apn : '',
        constructionMonitorPermitId: constructionMonitorPermitId,
      };
      onUseVerifiedData({
        formData: updatdData,
        attachments: {
          attachmentFiles,
          attachmentUrls: attachmentUrls.map((item) => ({
            name: item.name || '',
            url: item.url || '',
          })),
        },
      });
    } else if (state === ModalState.VERIFY_OWNER_MANUAL) {
      onManualDataUpdate({
        formData: newFormStateData,
        attachments: {
          attachmentFiles,
          attachmentUrls: attachmentUrls.map((item) => ({
            name: item.name || '',
            url: item.url || '',
          })),
        },
      });
    }
    setState(ModalState.VERIFY_JOB);
    onClose();
  };

  const handleRequestInfoSheet = () => {
    const newErrors = {} as VerifiedJobDataPick;
    if (formState.buyerId?.trim() === '' && editMode === 'EDIT') {
      newErrors.buyerId = 'Select a buyer.';
      setErrors(newErrors);
      setShowErrorAbove(true);
      return;
    }
    setShowErrorAbove(false);
    const foundSupplierBuyer = supplierBuyers.find((supBuy) => supBuy.id === formState.buyerId);
    const data = {
      ...formState,
      jobAddress: formState.jobAddress ?? '',
      jobCity: formState.jobCity ?? '',
      jobState: formState.jobState ?? '',
      jobZipCode: formState.jobZipCode ?? '',
      buyerId: formState.buyerId ?? '',
      jobNumber: formState.jobNumber ?? '',
      name: formState.name ?? '',
      buyerName: foundSupplierBuyer?.name ?? '',
      buyerAddress: foundSupplierBuyer?.address ?? '',
      buyerCity: foundSupplierBuyer?.city ?? '',
      buyerState: foundSupplierBuyer?.state ?? '',
      buyerZipCode: foundSupplierBuyer?.zipCode ?? '',
    };
    onRequestInfoSheet(data);
  };

  useEffect(() => {
    if (state === ModalState.VERIFY_OWNER || state === ModalState.VERIFY_OWNER_MANUAL) {
      const foundSupplierBuyer = supplierBuyers.find((supBuy) => supBuy.id === formState.buyerId);
      const addressData = [{ name: '', address: '', city: '', state: '', zipCode: '' }];

      setAddressFormState((prevState) => {
        const ownerState =
          state === ModalState.VERIFY_OWNER
            ? owners || supplierJobData?.owners || addressData
            : state === ModalState.VERIFY_OWNER_MANUAL && editMode !== 'EDIT'
            ? addressData
            : supplierJobData?.owners || addressData;
        const gcStates =
          state === ModalState.VERIFY_OWNER
            ? GCs || supplierJobData?.GCs || addressData
            : formState.customerType === 'GC'
            ? [
                {
                  name: foundSupplierBuyer?.name || '',
                  address: foundSupplierBuyer?.address || '',
                  city: foundSupplierBuyer?.city || '',
                  state: foundSupplierBuyer?.state || '',
                  zipCode: foundSupplierBuyer?.zipCode || '',
                },
              ]
            : state === ModalState.VERIFY_OWNER_MANUAL && editMode !== 'EDIT'
            ? addressData
            : supplierJobData?.GCs || addressData;
        return { ...prevState, owners: ownerState, GCs: gcStates };
      });
      onChange2({
        name: 'apn',
        value:
          state === ModalState.VERIFY_OWNER
            ? verifiedProperty?.apn || ''
            : verifiedProperty?.apn || '',
      });
      onChange2({ name: 'jobNumber', value: jobNumber || '' });
      onChange2({ name: 'jobAddress', value: jobAddress || '' });
      onChange2({ name: 'jobCity', value: jobCity || '' });
      onChange2({ name: 'jobState', value: jobState || '' });
      onChange2({ name: 'jobZipCode', value: jobZipCode || '' });
      onChange2({ name: 'name', value: name || '' });
      onChange2({ name: 'buyerId', value: buyerId || '' });
      onChange2({ name: 'customerJobNumber', value: customerJobNumber || '' });
      onChange2({
        name: 'estimatedMaterialAmount',
        value: formState.estimatedMaterialAmount || 0,
      });
      onChange2({
        name: 'jobType',
        value: state === ModalState.VERIFY_OWNER ? formState.jobType || 'commercial' : 'commercial',
      });
      onChange2({
        name: 'jobSector',
        value: state === ModalState.VERIFY_OWNER ? formState.jobSector || 'private' : 'private',
      });
      onChange2({ name: 'customerType', value: formState.customerType || 'SUB' });
      onChange2({ name: 'jobFurnishings', value: formState.jobFurnishings || '' });
      onChange2({ name: 'hasNoLender', value: formState.hasNoLender || false });
      onChange2({ name: 'isJobBonded', value: formState.isJobBonded || false });
      if (editMode === 'EDIT') {
        onChange2({ name: 'jobNumber', value: formState.jobNumber || '' });
        onChange2({ name: 'jobAddress', value: formState.jobAddress || '' });
        onChange2({ name: 'jobCity', value: formState.jobCity || '' });
        onChange2({ name: 'jobState', value: formState.jobState || '' });
        onChange2({ name: 'jobZipCode', value: formState.jobZipCode || '' });
        onChange2({ name: 'name', value: formState.name || '' });
        onChange2({ name: 'buyerId', value: formState.buyerId || '' });
        onChange2({ name: 'customerJobNumber', value: formState.customerJobNumber || '' });
      }
      if (formState.jobState === 'AZ') {
        onChange2({ name: 'jobCounty', value: formState.jobCounty || '' });
        onChange2({ name: 'jobLegalDescription', value: formState.jobLegalDescription || '' });
      }
    }
  }, [state]);

  const handleOwnerAndGCSame = (value: boolean) => {
    if (value) {
      const data = {
        owners:
          formAddressState.owners &&
          formAddressState.owners?.length > 0 &&
          formAddressState.owners[0].name !== ''
            ? formAddressState.owners
            : formAddressState.GCs,
        GCs:
          formAddressState.GCs &&
          formAddressState.GCs?.length > 0 &&
          formAddressState.GCs[0].name !== ''
            ? formAddressState.GCs
            : formAddressState.owners,
      };
      setAddressFormState((prevState) => ({
        ...prevState,
        ...data,
      }));
    }
    setOwnerAndGCSame(value);
  };

  useEffect(() => {
    if (supplierJobData) {
      let supplierDetails = {};
      if (editMode === 'EDIT') {
        supplierDetails = {
          jobAddress: supplierJobData.jobAddress ?? '',
          jobCity: supplierJobData.jobCity ?? '',
          jobState: getStateAbbreviation(supplierJobData.jobState || '') || '',
          jobZipCode: supplierJobData.jobZipCode ?? '',
          buyerId: supplierJobData.buyerId ?? '',
          jobNumber: supplierJobData.jobNumber ?? '',
          name: supplierJobData.name ?? '',
          customerJobNumber: supplierJobData.customerJobNumber ?? '',
        };
      }
      let moreDetails = {};
      if (supplierJobData.jobState === 'AZ') {
        moreDetails = {
          jobCounty: supplierJobData.jobCounty ?? '',
          jobLegalDescription: supplierJobData.jobLegalDescription ?? '',
        };
      }
      setAttachmentUrls(
        supplierJobData?.jobInfoSheetAttachments ? supplierJobData?.jobInfoSheetAttachments : []
      );
      setAttachmentFiles([]);
      setFormState({
        ...supplierDetails,
        apn: supplierJobData.apn,
        estimatedMaterialAmount: supplierJobData.estimatedMaterialAmount,
        jobType: supplierJobData.jobType,
        jobSector: supplierJobData.jobSector,
        customerType: supplierJobData.customerType,
        jobFurnishings: supplierJobData.jobFurnishings,
        hasNoLender: supplierJobData.hasNoLender,
        isJobBonded: supplierJobData.isJobBonded,
        isGCUnknown: supplierJobData?.isGCUnknown,
        isLenderUnknown: supplierJobData?.isLenderUnknown,
        isOwnerUnknown: supplierJobData?.isOwnerUnknown,
        isSurityUnknown: supplierJobData?.isSurityUnknown,
        ...moreDetails,
      });
      const addressData = [
        {
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
        },
      ];
      setAddressFormState({
        GCs:
          supplierJobData.GCs && supplierJobData.GCs?.length > 0
            ? supplierJobData.GCs
            : addressData,
        owners:
          supplierJobData.owners && supplierJobData.owners?.length > 0
            ? supplierJobData.owners
            : addressData,
        lenders:
          supplierJobData.lenders && supplierJobData.lenders?.length > 0
            ? supplierJobData.lenders
            : addressData,
        sureties:
          supplierJobData.sureties && supplierJobData.sureties?.length > 0
            ? supplierJobData.sureties
            : addressData,
      });
    }
  }, [supplierJobData]);

  const fetchedSupplierBuyers = useMemo(() => {
    if (supplierBuyers.length > 0) {
      return supplierBuyers;
    }
    return [];
  }, [supplierBuyers]);

  const [gcSuggestions, ownerSuggestions] = useMemo(() => {
    let gcData: SupplierJobAddressDetails[] = [];
    let ownerData: SupplierJobAddressDetails[] = [];
    if (supplierJobsMap) {
      const uniqueGCMap: { [x: string]: SupplierJobAddressDetails } = {};
      Object.values(supplierJobsMap).forEach((item) => {
        if (item.GCs) {
          item.GCs.forEach((gc) => {
            if (gc.name) {
              uniqueGCMap[gc.name.toLowerCase()] = gc;
            }
          });
        }
      });
      gcData = Object.values(uniqueGCMap);

      const uniqueOwnerMap: { [x: string]: SupplierJobAddressDetails } = {};
      Object.values(supplierJobsMap).forEach((item) => {
        if (item.owners) {
          item.owners.forEach((owner) => {
            if (owner.name) {
              uniqueOwnerMap[owner.name.toLowerCase()] = owner;
            }
          });
        }
      });
      ownerData = Object.values(uniqueOwnerMap);
    }
    return [gcData, ownerData];
  }, [supplierJobsMap, open]);

  const filteredGCSuggestionsOptions = (text: string) => {
    return gcSuggestions.filter(
      (option) => option.name && option.name.toLowerCase().includes(text.toLowerCase())
    );
  };
  const filteredOwnerSuggestionsOptions = (text: string) => {
    return ownerSuggestions.filter(
      (option) => option.name && option.name.toLowerCase().includes(text.toLowerCase())
    );
  };

  const handlePreviewAttachment = (url: string, fileType: string) => {
    if (fileType && fileType !== 'pdf') {
      setPreviewAttachmentUrl(url);
      setShowPreviewImage(true);
    } else {
      setPreviewAttachmentUrl(url);
      setIsAppPdfPreviewModalOpen(true);
    }
  };

  const handleDownloadAttachment = async (fileUrl: string, fileName: string) => {
    downloadFile(fileUrl ?? '', fileName);
  };

  return (
    <>
      <EditBuyerModal
        open={isEditBuyerModalOpen}
        buyer={selectedBuyer}
        handleClose={(editSuccess) => {
          if (editSuccess) {
            dispatch(getSupplierBuyers({ supplierId: currentSupplier?.id || '' }));
            if (onBuyerEdit) {
              onBuyerEdit();
            }
          }
          setIsEditBuyerModalOpen(false);
        }}
      />
      <AppImagePreviewModal
        open={showPreviewImage}
        handleClose={() => {
          setShowPreviewImage(false);
          setPreviewAttachmentUrl('');
        }}
        title="Job Info Sheet Attachment"
        url={previewattachmentUrl}
      />
      <AppPdfPreviewModal
        open={isAppPdfPreviewModalOpen}
        handleClose={() => {
          setIsAppPdfPreviewModalOpen(false);
        }}
        invoice={''}
        pdfType={'PRELIMINARY_NOTICE'}
        pdfList={[
          {
            url: previewattachmentUrl,
            date: new Date(),
            name: '',
          },
        ]}
        handleDownloadPdf={() => {}}
        isSigned={false}
        showPendingMessage={false}
        mode="Supplier"
        showHistoryButton={false}
      />
      <div className={`new-job-modal-content pt-3`}>
        <div className="new-job-modal-title-container">
          <h3 className="text-lg font-semibold">Verify Job</h3>
          <AppButton
            type="FLAT"
            buttonStyles={{ padding: '0px', margin: '5px 2px' }}
            icon={<X color={COLORS.GREY_300} size={30} />}
            onClick={onClose}
          />
        </div>
        <div className="content-job !overflow-y-auto mx-6 my-3 px-4">
          {editMode === 'ADD' && (
            <VerifySupplierJob
              jobAddress={jobAddress}
              jobCity={jobCity}
              jobState={jobState}
              jobZipCode={jobZipCode}
              owners={owners}
              verifiedProperty={verifiedProperty}
              verifyJobCheckBox={verifyJobCheckBox}
              GCs={GCs}
            />
          )}
          {editMode === 'EDIT' && (
            <div className="flex flex-col gap-2 w-full">
              <AppDropDown2
                isRequired
                label="Customer"
                errorText={errors.buyerId ? errors.buyerId : ''}
                value={formState.buyerId || ''}
                items={[
                  ...fetchedSupplierBuyers.map((buyer) => {
                    return {
                      label: buyer.name,
                      value: buyer.id,
                      labelInElement: (
                        <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                          <div className="text-sm">{`${buyer.name}`}</div>
                          <button
                            onClick={(e) => {
                              setIsEditBuyerModalOpen(true);
                              setSelectedBuyer(buyer);
                            }}
                            className="text-sm px-4 py-1 WHITE-BG font-bold TEXT_SECONDARY-CLR rounded absolute hidden group-hover:inline-block right-2">
                            Edit
                          </button>
                        </div>
                      ),
                    };
                  }),
                ]}
                onSelectChange={(item) => {
                  if (item && item?.value !== null) {
                    const foundSupplierBuyer = supplierBuyers.find(
                      (supBuy) => supBuy.id === item?.value
                    );
                    if (foundSupplierBuyer) {
                      onChange2({ name: 'buyerId', value: item.value });
                    }
                  }
                }}
              />
              <div className="flex flex-row justify-around gap-2">
                <div className="w-full">
                  <AppInputField
                    id="name"
                    label="Job Name"
                    isRequired={true}
                    value={formState.name}
                    errorText={errors.name ? errors.name : ''}
                    onTextChange={(text: string) => {
                      onChange2({ name: 'name', value: text });
                    }}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    id="number"
                    label="Your Job Number"
                    placeholder="12345"
                    value={formState.jobNumber}
                    onTextChange={(text) => {
                      onChange2({ name: 'jobNumber', value: text });
                    }}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    id="customerJobNumber"
                    label="Customer Job Number"
                    inputType="text"
                    value={formState.customerJobNumber}
                    onTextChange={(text) => {
                      onChange2({ name: 'customerJobNumber', value: text });
                    }}
                  />
                </div>
              </div>
              <div className="flex">
                <div className="w-full">
                  <AppInputField
                    id="address"
                    label="Job Address"
                    isRequired={true}
                    value={formState.jobAddress}
                    errorText={errors.jobAddress ? errors.jobAddress : ''}
                    onTextChange={(text) => {
                      onChange2({ name: 'jobAddress', value: text });
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-row justify-around gap-2 pb-3">
                <div className="w-full">
                  <AppInputField
                    id="city"
                    label="Job City"
                    isRequired={true}
                    value={formState.jobCity}
                    errorText={errors.jobCity ? errors.jobCity : ''}
                    onTextChange={(text) => {
                      onChange2({ name: 'jobCity', value: text });
                    }}
                  />
                </div>
                <div className="w-full">
                  <AppDropDown2
                    isRequired
                    label="Job State"
                    value={formState.jobState || null}
                    errorText={errors.jobState ? errors.jobState : ''}
                    items={[
                      ...Object.keys(allStates).map((stateName) => {
                        const key = stateName as keyof typeof allStates;
                        const stateKey = allStates[key];
                        return {
                          label: `${stateKey} - ${stateName}`,
                          value: stateKey,
                        };
                      }),
                    ]}
                    onSelectChange={(item) => {
                      if (item && item?.value !== null) {
                        const foundState = Object.values(getAllStates()).find(
                          (stateName) => stateName === item?.value
                        );
                        if (foundState) {
                          onChange2({ name: 'jobState', value: item.value });
                        }
                      }
                    }}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    id="zip"
                    label="Zip Code"
                    isRequired={true}
                    value={formState.jobZipCode}
                    errorText={errors.jobZipCode ? errors.jobZipCode : ''}
                    onTextChange={(text) => {
                      onChange2({ name: 'jobZipCode', value: text });
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {(state === ModalState.VERIFY_OWNER || state === ModalState.VERIFY_OWNER_MANUAL) && (
            <>
              <div className="pb-3">
                <div className="text-xs mb-1 font-semibold">Job Type</div>
                <div className="flex flex-row gap-3">
                  <div className="flex  items-center">
                    <div
                      className="flex items-center mr-1 cursor-pointer"
                      onClick={() => onChange2({ name: 'jobType', value: 'commercial' })}>
                      <div
                        className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                          formState.jobType === 'commercial'
                            ? 'PRIMARY_500-BORDER'
                            : 'GREY_500-BORDER'
                        }`}>
                        <div
                          className={`flex items-center h-2 w-2 rounded-full ${
                            formState.jobType === 'commercial' ? 'PRIMARY_500-BG ' : ''
                          }`}></div>
                      </div>
                    </div>
                    <div className="text-xs">Commercial</div>
                  </div>
                  <div className="flex  items-center">
                    <div
                      className="flex items-center mr-1 cursor-pointer"
                      onClick={() => onChange2({ name: 'jobType', value: 'residential' })}>
                      <div
                        className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                          formState.jobType === 'residential'
                            ? 'PRIMARY_500-BORDER'
                            : 'GREY_500-BORDER'
                        }`}>
                        <div
                          className={`flex items-center h-2 w-2 rounded-full ${
                            formState.jobType === 'residential' ? 'PRIMARY_500-BG ' : ''
                          }`}></div>
                      </div>
                    </div>
                    <div className="text-xs">Residential</div>
                  </div>
                </div>
              </div>
              <div className="pb-3">
                <div className="text-xs mb-1 font-semibold">Job Sector</div>
                <div className="flex flex-row gap-3">
                  <div className="flex  items-center">
                    <div
                      className="flex items-center mr-1 cursor-pointer"
                      onClick={() => onChange2({ name: 'jobSector', value: 'private' })}>
                      <div
                        className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                          formState.jobSector === 'private'
                            ? 'PRIMARY_500-BORDER'
                            : 'GREY_500-BORDER'
                        }`}>
                        <div
                          className={`flex items-center h-2 w-2 rounded-full ${
                            formState.jobSector === 'private' ? 'PRIMARY_500-BG ' : ''
                          }`}></div>
                      </div>
                    </div>
                    <div className="text-xs">Private</div>
                  </div>
                  <div className="flex  items-center">
                    <div
                      className="flex items-center mr-1 cursor-pointer"
                      onClick={() => onChange2({ name: 'jobSector', value: 'public' })}>
                      <div
                        className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                          formState.jobSector === 'public'
                            ? 'PRIMARY_500-BORDER'
                            : 'GREY_500-BORDER'
                        }`}>
                        <div
                          className={`flex items-center h-2 w-2 rounded-full ${
                            formState.jobSector === 'public' ? 'PRIMARY_500-BG ' : ''
                          }`}></div>
                      </div>
                    </div>
                    <div className="text-xs">Public</div>
                  </div>
                </div>
              </div>
              <div className="flex flex-row  pb-3">
                <div className="w-full">
                  <AppInputField
                    id="jobFurnishings"
                    label="Job Furnishings"
                    placeholder="Job Furnishings"
                    isRequired={true}
                    value={formState.jobFurnishings}
                    errorText={errors.jobFurnishings ? `${errors.jobFurnishings}` : ''}
                    onTextChange={(text) => {
                      onChange2({ name: 'jobFurnishings', value: text });
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-row  pb-3">
                <div className="w-full">
                  <AppInputField
                    id="estimatedMaterialAmount"
                    label="Estimated Material Amount"
                    placeholder="12345"
                    inputType="number"
                    isRequired={true}
                    value={formState.estimatedMaterialAmount}
                    errorText={
                      errors.estimatedMaterialAmount ? `${errors.estimatedMaterialAmount}` : ''
                    }
                    onTextChange={(text) => {
                      onChange2({ name: 'estimatedMaterialAmount', value: text });
                    }}
                  />
                </div>
              </div>
              {formState.jobState === 'AZ' && (
                <div className="flex flex-row  pb-3">
                  <div className="w-full">
                    <AppInputField
                      id="jobCounty"
                      label="Job County"
                      isRequired={true}
                      placeholder="Job County"
                      value={formState.jobCounty}
                      errorText={errors.jobCounty ? `${errors.jobCounty}` : ''}
                      onTextChange={(text) => {
                        onChange2({ name: 'jobCounty', value: text });
                      }}
                    />
                  </div>
                </div>
              )}
              {formState.jobState === 'AZ' && (
                <div className="flex flex-row  pb-3">
                  <div className="w-full">
                    <AppInputField
                      id="jobLegalDescription"
                      label="Job Legal Description"
                      isRequired={true}
                      placeholder="Job Legal Description"
                      value={formState.jobLegalDescription}
                      errorText={errors.jobLegalDescription ? `${errors.jobLegalDescription}` : ''}
                      onTextChange={(text) => {
                        onChange2({ name: 'jobLegalDescription', value: text });
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="flex flex-row  pb-3">
                <div className="w-full">
                  <AppInputField
                    id="apn"
                    label="Property APN"
                    placeholder="12345"
                    isRequired={false}
                    value={formState.apn}
                    errorText={errors.apn ? errors.apn : ''}
                    onTextChange={(text) => {
                      onChange2({ name: 'apn', value: text });
                    }}
                  />
                </div>
              </div>
              <div className="flex w-full pb-3">
                <AppCheckBox
                  className="mr-2"
                  isChecked={formState.customerType === 'GC'}
                  onClick={(val: boolean) => {
                    onChange2({ name: 'customerType', value: val ? 'GC' : 'SUB' });
                  }}
                />
                <div className="text-black text-sm">
                  My customer is the general contractor on the project, not a subcontractor.
                </div>
              </div>
              <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                <div className="text-sm font-semibold">GC</div>
                {formAddressState.GCs.map((entry, index) => {
                  const gcSuggestedOptions = filteredGCSuggestionsOptions(entry.name);
                  return (
                    <div
                      key={`gc-address-input-${index}`}
                      className="border-b GREY_300-BORDER pb-2 mb-1">
                      {formAddressState.GCs.length > 1 && (
                        <div className="w-full flex justify-end">
                          <button
                            type="button"
                            className={
                              formState?.isGCUnknown
                                ? 'GREY_500-CLR text-sm self-start'
                                : 'text-red-500 text-sm self-start'
                            }
                            onClick={() => handleRemoveAddress(index, 'GCs')}
                            disabled={formState?.isGCUnknown}>
                            Remove
                          </button>
                        </div>
                      )}
                      <div className="flex flex-row  pb-3">
                        <div className="w-full relative" ref={gcRef}>
                          <AppInputField
                            id={`gc-${index}`}
                            label="GC Name"
                            inputType="text"
                            placeholder="GC Name"
                            isDisabled={formState?.isGCUnknown}
                            isRequired={true}
                            value={formState?.isGCUnknown ? '' : entry.name}
                            onFocus={() => setIsGCAutoCompleteOpen(`focus-gc-${index}`)}
                            errorText={
                              formAddressError.GCs !== '' &&
                              entry.name === '' &&
                              !formState?.isGCUnknown
                                ? 'GC Name is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'GCs',
                                field: 'name',
                                index: index,
                              });
                            }}
                          />
                          {isGCAutoCompleteOpen === `focus-gc-${index}` &&
                            gcSuggestedOptions.length > 0 && (
                              <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-md z-50">
                                <div className="p-2">
                                  {gcSuggestedOptions.length > 0 ? (
                                    gcSuggestedOptions.map((option, i) => (
                                      <div
                                        key={`${option.name}_${i}-gc-option`}
                                        onClick={() => {
                                          handleUpdateSupplierJobAddress({
                                            value: option.name,
                                            key: 'GCs',
                                            field: 'name',
                                            index: index,
                                          });
                                          handleUpdateSupplierJobAddress({
                                            value: option.address,
                                            key: 'GCs',
                                            field: 'address',
                                            index: index,
                                          });
                                          handleUpdateSupplierJobAddress({
                                            value: option.city,
                                            key: 'GCs',
                                            field: 'city',
                                            index: index,
                                          });
                                          handleUpdateSupplierJobAddress({
                                            value: option.state,
                                            key: 'GCs',
                                            field: 'state',
                                            index: index,
                                          });
                                          handleUpdateSupplierJobAddress({
                                            value: option.zipCode,
                                            key: 'GCs',
                                            field: 'zipCode',
                                            index: index,
                                          });

                                          setIsGCAutoCompleteOpen('');
                                        }}
                                        className="p-1 hover:bg-gray-100 cursor-pointer">
                                        {option.name}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="p-2 text-gray-500"></div>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="flex flex-row  pb-3">
                        <div className="w-full ">
                          <AppInputField
                            id={`gc-address-${index}`}
                            label="GC Address"
                            inputType="text"
                            placeholder="GC Address"
                            isRequired={true}
                            isDisabled={formState?.isGCUnknown}
                            value={formState?.isGCUnknown ? '' : entry.address}
                            errorText={
                              formAddressError.GCs !== '' &&
                              entry.address === '' &&
                              !formState?.isGCUnknown
                                ? 'GC Address is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'GCs',
                                field: 'address',
                                index: index,
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-row w-full gap-1">
                        <AppInputField
                          id={`gc-city-${index}`}
                          label="GC City"
                          inputType="text"
                          placeholder="GC City"
                          isRequired={true}
                          isDisabled={formState?.isGCUnknown}
                          value={formState?.isGCUnknown ? '' : entry.city}
                          errorText={
                            formAddressError.GCs !== '' &&
                            entry.city === '' &&
                            !formState?.isGCUnknown
                              ? 'GC City is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'GCs',
                              field: 'city',
                              index: index,
                            });
                          }}
                        />
                        <div className="w-full">
                          <AppDropDown2
                            isRequired
                            label="GC State"
                            disabled={formState?.isGCUnknown}
                            value={formState?.isGCUnknown ? null : entry.state || null}
                            errorText={
                              formAddressError.GCs !== '' &&
                              entry.state === '' &&
                              !formState?.isGCUnknown
                                ? 'GC State is required'
                                : ''
                            }
                            items={[
                              ...Object.keys(allStates).map((stateName) => {
                                const key = stateName as keyof typeof allStates;
                                const stateKey = allStates[key];
                                return {
                                  label: `${stateKey} - ${stateName}`,
                                  value: stateKey,
                                };
                              }),
                            ]}
                            onSelectChange={(item) => {
                              if (item && item?.value !== null) {
                                const foundState = Object.values(getAllStates()).find(
                                  (stateName) => stateName === item?.value
                                );
                                if (foundState) {
                                  handleUpdateSupplierJobAddress({
                                    value: item.value,
                                    key: 'GCs',
                                    field: 'state',
                                    index: index,
                                  });
                                }
                              }
                            }}
                          />
                        </div>
                        <AppInputField
                          id={`gc-zip-${index}`}
                          label="GC Zip Code"
                          inputType="text"
                          placeholder="12345"
                          isRequired={true}
                          isDisabled={formState?.isGCUnknown}
                          value={formState?.isGCUnknown ? '' : entry.zipCode}
                          errorText={
                            formAddressError.GCs !== '' &&
                            entry.zipCode === '' &&
                            !formState?.isGCUnknown
                              ? 'GC Zip Code is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'GCs',
                              field: 'zipCode',
                              index: index,
                            });
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="w-full flex mb-2 justify-end">
                  <AppButton
                    text="Add GC"
                    onClick={() => handleAddAddress('GCs')}
                    isDisabled={formState?.isGCUnknown}
                    icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                    buttonStyles={{
                      height: '30px',
                      borderRadius: '5px',
                    }}
                  />
                </div>
                <div
                  className="flex  items-center cursor-pointer w-fit"
                  onClick={() => {
                    onChange2({ name: 'isGCUnknown', value: !formState?.isGCUnknown });
                  }}>
                  <AppCheckBox className="mr-2" isChecked={formState.isGCUnknown} />
                  <div className="text-black text-sm">GC Unknown</div>
                </div>
              </div>
              <div className="flex w-full py-1">
                <AppCheckBox
                  className="mr-2"
                  isChecked={ownerAndGCSame}
                  onClick={handleOwnerAndGCSame}
                />
                <div className="text-black text-sm">Owner and GC are the same.</div>
              </div>
              <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                <div className="text-sm font-semibold">Owner</div>
                {formAddressState.owners.map((entry, index) => {
                  const ownerSuggestedOptions = filteredOwnerSuggestionsOptions(entry.name);
                  return (
                    <div
                      key={`owner-address-input-${index}`}
                      className="border-b GREY_300-BORDER pb-2 mb-1">
                      {formAddressState.owners.length > 1 && (
                        <div className="w-full flex justify-end">
                          <button
                            type="button"
                            className={
                              formState?.isOwnerUnknown
                                ? 'GREY_500-CLR text-sm self-start'
                                : 'text-red-500 text-sm self-start'
                            }
                            onClick={() => handleRemoveAddress(index, 'owners')}
                            disabled={formState?.isOwnerUnknown}>
                            Remove
                          </button>
                        </div>
                      )}
                      <div className="flex flex-row  pb-3">
                        <div className="w-full relative" ref={ownerRef}>
                          <AppInputField
                            id={`owner-${index}`}
                            label="Owner Name"
                            inputType="text"
                            placeholder="Owner Name"
                            isDisabled={formState?.isOwnerUnknown}
                            isRequired={true}
                            value={formState?.isOwnerUnknown ? '' : entry.name}
                            onFocus={() => setIsOwnerAutoCompleteOpen(`focus-owner-${index}`)}
                            errorText={
                              formAddressError.owners !== '' &&
                              entry.name === '' &&
                              !formState?.isOwnerUnknown
                                ? 'Owner Name is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'owners',
                                field: 'name',
                                index: index,
                              });
                            }}
                          />
                          {isOwnerAutoCompleteOpen === `focus-owner-${index}` &&
                            ownerSuggestedOptions.length > 0 && (
                              <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-md z-50">
                                <div className="p-2">
                                  {ownerSuggestedOptions.length > 0 ? (
                                    ownerSuggestedOptions.map((option) => (
                                      <div
                                        key={`${option.name}_${index}-owner-option`}
                                        onClick={() => {
                                          handleUpdateSupplierJobAddress({
                                            value: option.name,
                                            key: 'owners',
                                            field: 'name',
                                            index: index,
                                          });
                                          handleUpdateSupplierJobAddress({
                                            value: option.address,
                                            key: 'owners',
                                            field: 'address',
                                            index: index,
                                          });
                                          handleUpdateSupplierJobAddress({
                                            value: option.city,
                                            key: 'owners',
                                            field: 'city',
                                            index: index,
                                          });
                                          handleUpdateSupplierJobAddress({
                                            value: option.state,
                                            key: 'owners',
                                            field: 'state',
                                            index: index,
                                          });
                                          handleUpdateSupplierJobAddress({
                                            value: option.zipCode,
                                            key: 'owners',
                                            field: 'zipCode',
                                            index: index,
                                          });

                                          setIsOwnerAutoCompleteOpen('');
                                        }}
                                        className="p-1 hover:bg-gray-100 cursor-pointer">
                                        {option.name}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="p-2 text-gray-500"></div>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="flex flex-row  pb-3">
                        <div className="w-full ">
                          <AppInputField
                            id={`owner-address-${index}`}
                            label="Owner Address"
                            inputType="text"
                            placeholder="Owner Address"
                            isRequired={true}
                            isDisabled={formState?.isOwnerUnknown}
                            value={formState?.isOwnerUnknown ? '' : entry.address}
                            errorText={
                              formAddressError.owners !== '' &&
                              entry.address === '' &&
                              !formState?.isOwnerUnknown
                                ? 'Owner Address is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'owners',
                                field: 'address',
                                index: index,
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-row w-full gap-1">
                        <AppInputField
                          id={`owner-city-${index}`}
                          label="Owner City"
                          inputType="text"
                          placeholder="Owner City"
                          isRequired={true}
                          isDisabled={formState?.isOwnerUnknown}
                          value={formState?.isOwnerUnknown ? '' : entry.city}
                          errorText={
                            formAddressError.owners !== '' &&
                            entry.city === '' &&
                            !formState?.isOwnerUnknown
                              ? 'Owner City is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'owners',
                              field: 'city',
                              index: index,
                            });
                          }}
                        />
                        <div className="w-full">
                          <AppDropDown2
                            isRequired
                            label="Owner State"
                            disabled={formState?.isOwnerUnknown}
                            value={formState?.isOwnerUnknown ? null : entry.state || null}
                            errorText={
                              formAddressError.owners !== '' &&
                              entry.state === '' &&
                              !formState?.isOwnerUnknown
                                ? 'Owner State is required'
                                : ''
                            }
                            items={[
                              ...Object.keys(allStates).map((stateName) => {
                                const key = stateName as keyof typeof allStates;
                                const stateKey = allStates[key];
                                return {
                                  label: `${stateKey} - ${stateName}`,
                                  value: stateKey,
                                };
                              }),
                            ]}
                            onSelectChange={(item) => {
                              if (item && item?.value !== null) {
                                const foundState = Object.values(getAllStates()).find(
                                  (stateName) => stateName === item?.value
                                );
                                if (foundState) {
                                  handleUpdateSupplierJobAddress({
                                    value: item.value,
                                    key: 'owners',
                                    field: 'state',
                                    index: index,
                                  });
                                }
                              }
                            }}
                          />
                        </div>
                        <AppInputField
                          id={`owner-zip-${index}`}
                          label="Owner Zip Code"
                          inputType="text"
                          placeholder="12345"
                          isRequired={true}
                          isDisabled={formState?.isOwnerUnknown}
                          value={formState?.isOwnerUnknown ? '' : entry.zipCode}
                          errorText={
                            formAddressError.owners !== '' &&
                            entry.zipCode === '' &&
                            !formState?.isOwnerUnknown
                              ? 'Owner Zip Code is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'owners',
                              field: 'zipCode',
                              index: index,
                            });
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="w-full flex mb-2 justify-end">
                  <AppButton
                    text="Add Owner"
                    onClick={() => handleAddAddress('owners')}
                    icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                    buttonStyles={{
                      height: '30px',
                      borderRadius: '5px',
                    }}
                  />
                </div>
                <div
                  className="flex  items-center cursor-pointer w-fit"
                  onClick={() => {
                    onChange2({ name: 'isOwnerUnknown', value: !formState?.isOwnerUnknown });
                  }}>
                  <AppCheckBox className="mr-2" isChecked={formState?.isOwnerUnknown} />
                  <div className="text-black text-sm">Owner Unknown</div>
                </div>
              </div>
              <div className="flex w-full py-1 items-center">
                <AppCheckBox
                  className="mr-2"
                  isChecked={formState.hasNoLender}
                  onClick={(val: boolean) => {
                    onChange2({ name: 'hasNoLender', value: val });
                  }}
                />
                <div className="text-black text-sm">There is no lender on this job</div>
              </div>
              {!formState.hasNoLender && (
                <div>
                  <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                    <div className="text-sm font-semibold">Lender</div>
                    {formAddressState.lenders &&
                      formAddressState.lenders.map((entry, index) => (
                        <div
                          key={`lender-address-input-${index}`}
                          className="border-b GREY_300-BORDER pb-2 mb-1">
                          {formAddressState.lenders && formAddressState.lenders.length > 1 && (
                            <div className="w-full flex justify-end">
                              <button
                                type="button"
                                className={
                                  formState?.isLenderUnknown
                                    ? 'GREY_500-CLR text-sm self-start'
                                    : 'text-red-500 text-sm self-start'
                                }
                                onClick={() => handleRemoveAddress(index, 'lenders')}
                                disabled={formState?.isLenderUnknown}>
                                Remove
                              </button>
                            </div>
                          )}
                          <div className="flex flex-row  pb-3">
                            <div className="w-full">
                              <AppInputField
                                id={`lender-${index}`}
                                label="Lender Name"
                                inputType="text"
                                placeholder="Lender Name"
                                isDisabled={formState?.isLenderUnknown}
                                isRequired={!formState.hasNoLender}
                                value={formState?.isLenderUnknown ? '' : entry.name}
                                errorText={
                                  formAddressError.lenders !== '' &&
                                  entry.name === '' &&
                                  !formState?.isLenderUnknown
                                    ? 'Lender Name is required'
                                    : ''
                                }
                                onTextChange={(text) => {
                                  handleUpdateSupplierJobAddress({
                                    value: text,
                                    key: 'lenders',
                                    field: 'name',
                                    index: index,
                                  });
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex flex-row  pb-3">
                            <div className="w-full ">
                              <AppInputField
                                id={`lender-address-${index}`}
                                label="Lender Address"
                                inputType="text"
                                placeholder="Lender Address"
                                isRequired={!formState.hasNoLender}
                                isDisabled={formState?.isLenderUnknown}
                                value={formState?.isLenderUnknown ? '' : entry.address}
                                errorText={
                                  formAddressError.lenders !== '' &&
                                  entry.address === '' &&
                                  !formState?.isLenderUnknown
                                    ? 'Lender Address is required'
                                    : ''
                                }
                                onTextChange={(text) => {
                                  handleUpdateSupplierJobAddress({
                                    value: text,
                                    key: 'lenders',
                                    field: 'address',
                                    index: index,
                                  });
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex flex-row  pb-3 gap-1">
                            <AppInputField
                              id={`lender-city-${index}`}
                              label="Lender City"
                              inputType="text"
                              placeholder="Lender City"
                              isRequired={!formState.hasNoLender}
                              isDisabled={formState?.isLenderUnknown}
                              value={formState?.isLenderUnknown ? '' : entry.city}
                              errorText={
                                formAddressError.lenders !== '' &&
                                entry.city === '' &&
                                !formState?.isLenderUnknown
                                  ? 'Lender City is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'lenders',
                                  field: 'city',
                                  index: index,
                                });
                              }}
                            />
                            <AppDropDown2
                              isRequired
                              label="Lender State"
                              disabled={formState?.isLenderUnknown}
                              value={formState?.isLenderUnknown ? null : entry.state || null}
                              errorText={
                                formAddressError.lenders !== '' &&
                                entry.state === '' &&
                                !formState?.isLenderUnknown
                                  ? 'Lender State is required'
                                  : ''
                              }
                              items={[
                                ...Object.keys(allStates).map((stateName) => {
                                  const key = stateName as keyof typeof allStates;
                                  const stateKey = allStates[key];
                                  return {
                                    label: `${stateKey} - ${stateName}`,
                                    value: stateKey,
                                  };
                                }),
                              ]}
                              onSelectChange={(item) => {
                                if (item && item?.value !== null) {
                                  const foundState = Object.values(getAllStates()).find(
                                    (stateName) => stateName === item?.value
                                  );
                                  if (foundState) {
                                    handleUpdateSupplierJobAddress({
                                      value: item.value,
                                      key: 'lenders',
                                      field: 'state',
                                      index: index,
                                    });
                                  }
                                }
                              }}
                            />
                            <AppInputField
                              id={`lender-zip-${index}`}
                              label="Lender Zip"
                              inputType="text"
                              placeholder="Lender Zip"
                              isRequired={!formState.hasNoLender}
                              isDisabled={formState?.isLenderUnknown}
                              value={formState?.isLenderUnknown ? '' : entry.zipCode}
                              errorText={
                                formAddressError.lenders !== '' &&
                                entry.zipCode === '' &&
                                !formState?.isLenderUnknown
                                  ? 'Lender Zip Code is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'lenders',
                                  field: 'zipCode',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    <div className="w-full flex mb-2 justify-end">
                      <AppButton
                        text="Add Lender"
                        onClick={() => handleAddAddress('lenders')}
                        icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                        buttonStyles={{
                          height: '30px',
                          borderRadius: '5px',
                        }}
                        isDisabled={formState?.isLenderUnknown}
                      />
                    </div>
                    <div
                      className="flex  items-center cursor-pointer w-fit"
                      onClick={() => {
                        onChange2({ name: 'isLenderUnknown', value: !formState?.isLenderUnknown });
                      }}>
                      <AppCheckBox className="mr-2" isChecked={formState?.isLenderUnknown} />
                      <div className="text-black text-sm">Lender Unknown</div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex w-full  items-center">
                <AppCheckBox
                  className="mr-2"
                  isChecked={formState.isJobBonded}
                  onClick={(val: boolean) => {
                    onChange2({ name: 'isJobBonded', value: val });
                  }}
                />
                <div className="text-black text-sm">Is this a bonded job?</div>
              </div>
              {formState.isJobBonded && (
                <div>
                  <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                    <div className="text-sm font-semibold">Surety</div>
                    {formAddressState.sureties &&
                      formAddressState.sureties.map((entry, index) => (
                        <div
                          key={`sureties-address-${index}`}
                          className="border-b GREY_300-BORDER pb-2 mb-1">
                          {formAddressState.sureties && formAddressState.sureties?.length > 1 && (
                            <div className="w-full flex justify-end">
                              <button
                                type="button"
                                className={
                                  formState?.isSurityUnknown
                                    ? 'GREY_500-CLR text-sm self-start'
                                    : 'text-red-500 text-sm self-start'
                                }
                                onClick={() => handleRemoveAddress(index, 'sureties')}
                                disabled={formState?.isSurityUnknown}>
                                Remove
                              </button>
                            </div>
                          )}
                          <div className="flex flex-row  pb-3">
                            <div className="w-full">
                              <AppInputField
                                id={`surety-${index}`}
                                label="Surety"
                                inputType="text"
                                placeholder="Surety"
                                isRequired={true}
                                isDisabled={formState?.isSurityUnknown}
                                value={formState?.isSurityUnknown ? '' : entry.name}
                                errorText={
                                  formAddressError.sureties !== '' &&
                                  entry.name === '' &&
                                  !formState?.isSurityUnknown
                                    ? 'Surety Name is required'
                                    : ''
                                }
                                onTextChange={(text) => {
                                  handleUpdateSupplierJobAddress({
                                    value: text,
                                    key: 'sureties',
                                    field: 'name',
                                    index: index,
                                  });
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex flex-row  pb-3">
                            <div className="w-full">
                              <AppInputField
                                id={`suretyAddress-${index}`}
                                label="Surety Address"
                                inputType="text"
                                placeholder="Surety Address"
                                isRequired={true}
                                isDisabled={formState?.isSurityUnknown}
                                value={formState?.isSurityUnknown ? '' : entry.address}
                                errorText={
                                  formAddressError.sureties !== '' &&
                                  entry.address === '' &&
                                  !formState?.isSurityUnknown
                                    ? 'Surety Address is required'
                                    : ''
                                }
                                onTextChange={(text) => {
                                  handleUpdateSupplierJobAddress({
                                    value: text,
                                    key: 'sureties',
                                    field: 'address',
                                    index: index,
                                  });
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex flex-row  pb-3 gap-2">
                            <div className="w-full">
                              <AppInputField
                                id={`suretyCity-${index}`}
                                label="Surety City"
                                inputType="text"
                                placeholder="Surety City"
                                isRequired={true}
                                isDisabled={formState?.isSurityUnknown}
                                value={formState?.isSurityUnknown ? '' : entry.city}
                                errorText={
                                  formAddressError.sureties !== '' &&
                                  entry.city === '' &&
                                  !formState?.isSurityUnknown
                                    ? 'Surety City is required'
                                    : ''
                                }
                                onTextChange={(text) => {
                                  handleUpdateSupplierJobAddress({
                                    value: text,
                                    key: 'sureties',
                                    field: 'city',
                                    index: index,
                                  });
                                }}
                              />
                            </div>
                            <div className="w-full">
                              <AppDropDown2
                                label="Surety State"
                                isRequired={true}
                                disabled={formState?.isSurityUnknown}
                                value={formState?.isSurityUnknown ? null : entry.state || null}
                                errorText={
                                  formAddressError.sureties !== '' &&
                                  entry.state === '' &&
                                  !formState?.isSurityUnknown
                                    ? 'Surety State is required'
                                    : ''
                                }
                                items={[
                                  ...Object.keys(allStates).map((stateName) => {
                                    const key = stateName as keyof typeof allStates;
                                    const stateKey = allStates[key];
                                    return {
                                      label: `${stateKey} - ${stateName}`,
                                      value: stateKey,
                                    };
                                  }),
                                ]}
                                onSelectChange={(item) => {
                                  if (item && item?.value !== null) {
                                    const foundState = Object.values(getAllStates()).find(
                                      (stateName) => stateName === item?.value
                                    );
                                    if (foundState) {
                                      handleUpdateSupplierJobAddress({
                                        value: item.value,
                                        key: 'sureties',
                                        field: 'state',
                                        index: index,
                                      });
                                    }
                                  }
                                }}
                              />
                            </div>
                            <div className="w-full">
                              <AppInputField
                                id={`suretyZip-${index}`}
                                label="Surety Zip"
                                inputType="text"
                                placeholder="Surety Zip"
                                isRequired={true}
                                isDisabled={formState?.isSurityUnknown}
                                value={formState?.isSurityUnknown ? '' : entry.zipCode}
                                errorText={
                                  formAddressError.sureties !== '' &&
                                  entry.zipCode === '' &&
                                  !formState?.isSurityUnknown
                                    ? 'Surety Zip Code is required'
                                    : ''
                                }
                                onTextChange={(text) => {
                                  handleUpdateSupplierJobAddress({
                                    value: text,
                                    key: 'sureties',
                                    field: 'zipCode',
                                    index: index,
                                  });
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex flex-row  pb-3">
                            <div className="w-full">
                              <AppInputField
                                id={`bondNumber-${index}`}
                                label="Bond Number"
                                inputType="text"
                                placeholder="Bond Number"
                                isRequired={true}
                                isDisabled={formState?.isSurityUnknown}
                                value={formState?.isSurityUnknown ? '' : entry.bondNumber}
                                errorText={
                                  formAddressError.sureties !== '' &&
                                  entry.bondNumber === '' &&
                                  !formState?.isSurityUnknown
                                    ? 'Bond Number is required'
                                    : ''
                                }
                                onTextChange={(text) => {
                                  handleUpdateSupplierJobAddress({
                                    value: text,
                                    key: 'sureties',
                                    field: 'bondNumber',
                                    index: index,
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    <div className="w-full flex mb-2 justify-end">
                      <AppButton
                        text="Add Surety"
                        onClick={() => handleAddAddress('sureties')}
                        icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                        buttonStyles={{
                          height: '30px',
                          borderRadius: '5px',
                        }}
                        isDisabled={formState?.isSurityUnknown}
                      />
                    </div>
                    <div
                      className="flex  items-center cursor-pointer w-fit"
                      onClick={() => {
                        onChange2({ name: 'isSurityUnknown', value: !formState?.isSurityUnknown });
                      }}>
                      <AppCheckBox className="mr-2" isChecked={formState?.isSurityUnknown} />
                      <div className="text-black text-sm">Surety Unknown</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="w-full py-3">
                <div className="text-xs mb-0.5  whitespace-nowrap font-medium">Attachments</div>
                <AppFilePicker
                  onSelectFile={(files: File[], fileUrls?: { name: string; url: string }[]) => {
                    setAttachmentFiles(files);
                  }}
                />
                {attachmentUrls.length > 0 && <h2>Attached Files:</h2>}
                {attachmentUrls.length > 0 &&
                  attachmentUrls.map((attachment, i) => {
                    return (
                      <div key={`${i}oo`} className="flex  items-center  justify-between border-b">
                        <p className="text-xs">{attachment.name}</p>
                        <div className="flex items-center">
                          {attachment?.fileType &&
                            (attachment?.fileType === 'pdf' ||
                              attachment?.fileType === 'png' ||
                              attachment?.fileType === 'jpg' ||
                              attachment?.fileType === 'jpeg') && (
                              <AppButton
                                type="TERTIARY"
                                buttonStyles={{
                                  padding: '4px',
                                  margin: '5px 4px 5px 0px',
                                  width: '20px',
                                  height: '20px',
                                }}
                                icon={<Eye color={COLORS.GREY_500} size={14} />}
                                onClick={() => {
                                  handlePreviewAttachment(
                                    attachment?.url ?? '',
                                    attachment?.fileType ?? ''
                                  );
                                }}
                              />
                            )}
                          <AppButton
                            type="TERTIARY"
                            buttonStyles={{
                              padding: '4px',
                              margin: '5px 4px 5px 0px',
                              width: '20px',
                              height: '20px',
                            }}
                            onClick={(e: any) => {
                              e.preventDefault();
                              handleDownloadAttachment(
                                attachment?.url ?? '',
                                attachment.name ?? 'attachment.pdf'
                              );
                            }}
                            icon={<Download color={COLORS.GREY_500} size={14} />}
                          />
                          <AppButton
                            type="TERTIARY"
                            buttonStyles={{
                              padding: '4px',
                              margin: '5px 4px 5px 0px',
                              width: '20px',
                              height: '20px',
                            }}
                            onClick={(e: any) => {
                              e.preventDefault();
                              setAttachmentUrls([...attachmentUrls.filter((item, ii) => ii !== i)]);
                            }}
                            icon={<Trash color={COLORS.ERROR_500} size={14} />}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col w-full mb-3 px-3">
          <hr className="h-px my-2 bg-gray-200 border-0 dark:bg-gray-200"></hr>
          {showErrorAbove && (
            <div className="my-1 text-xs ERROR_500-CLR text-center">Please see errors above</div>
          )}
          <div className="flex flex-row  px-6 ">
            {state === ModalState.VERIFY_JOB && (
              <>
                <AppButton text="Back" type="TERTIARY" onClick={handleBack} />
                <div className="flex justify-end w-full gap-3">
                  <AppButton
                    text="Manually update information"
                    type="SECONDARY"
                    onClick={changeManualState}
                  />
                  {verifiedProperty && (
                    <AppButton text="Use this data" type="PRIMARY" onClick={changeAutoState} />
                  )}
                </div>
              </>
            )}
            {(state === ModalState.VERIFY_OWNER || state === ModalState.VERIFY_OWNER_MANUAL) && (
              <div
                className={twMerge(
                  'flex flex-row gap-3',
                  editMode === 'EDIT' && currentSupplier?.ownerVerificationEnabled
                    ? 'w-[460px] justify-between'
                    : 'gap-3'
                )}>
                <AppButton
                  text={editMode === 'EDIT' ? 'Close' : 'Back'}
                  type="TERTIARY"
                  onClick={() => {
                    if (editMode === 'EDIT') {
                      onClose();
                    } else {
                      handleBack();
                    }
                  }}
                />
                {editMode === 'EDIT' && (
                  <AppButton
                    text="Request Job Info Sheet"
                    type="SECONDARY"
                    buttonStyles={{
                      height: '40px',
                    }}
                    onClick={handleRequestInfoSheet}
                  />
                )}
                <div className="flex flex-row gap-3">
                  {editMode === 'EDIT' && currentSupplier?.ownerVerificationEnabled && (
                    <AppButton
                      text="Verify"
                      type="SECONDARY"
                      onClick={() => {
                        if (!validateInputsVerify()) {
                          setShowErrorAbove(true);
                          return;
                        }
                        setShowErrorAbove(false);
                        onVerify({ ...formState, ...formAddressState }, supplierJobData);
                      }}
                      isLoading={verifying}
                      isDisabled={verifying}
                      buttonStyles={{
                        width: '80px',
                        height: '40px',
                      }}
                    />
                  )}
                  <AppButton text="Save" type="PRIMARY" onClick={onSubmitData} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SupplierVerifyJobForm;
